import jsPDF from 'jspdf';
import { ShiftaskProviderEvaluation, calculateAverageScore } from '@/hooks/useShiftaskProviderEvaluation';

const metrics = [
  { key: 'response_time', labelEl: 'Χρόνος Απόκρισης' },
  { key: 'incident_handling', labelEl: 'Διαχείριση Συμβάντων' },
  { key: 'guard_quality', labelEl: 'Ποιότητα Φρουρών' },
  { key: 'professionalism', labelEl: 'Επαγγελματισμός' },
  { key: 'communication', labelEl: 'Επικοινωνία' },
  { key: 'technology_satisfaction', labelEl: 'Ικανοποίηση από Τεχνολογία' },
  { key: 'reporting_quality', labelEl: 'Ποιότητα Αναφορών' },
  { key: 'innovation', labelEl: 'Καινοτομία' },
  { key: 'value_for_money', labelEl: 'Αξία για Χρήματα' },
  { key: 'contract_flexibility', labelEl: 'Ευελιξία Συμβολαίου' },
  { key: 'account_management', labelEl: 'Διαχείριση Λογαριασμού' },
];

export const generateProviderEvaluationPDF = async (
  evaluation: ShiftaskProviderEvaluation,
  clientName: string,
  appName: string,
  includeMitigations: boolean = false
) => {
  const doc = new jsPDF();
  let yPos = 20;

  // Header
  doc.setFontSize(20);
  doc.text(`${appName} - Αξιολόγηση Παρόχου`, 20, yPos);
  yPos += 15;

  // Client and period info
  doc.setFontSize(12);
  doc.text(`Πελάτης: ${clientName}`, 20, yPos);
  yPos += 10;
  doc.text(`Περίοδος: ${evaluation.evaluation_period} ${evaluation.evaluation_year}`, 20, yPos);
  yPos += 10;
  doc.text(`Υποβολή: ${new Date(evaluation.submitted_at).toLocaleString('el-GR')}`, 20, yPos);
  yPos += 15;

  // Average score
  const avgScore = calculateAverageScore(evaluation);
  doc.setFontSize(14);
  doc.text(`Μέσος Όρος: ${avgScore.toFixed(1)}/5`, 20, yPos);
  yPos += 15;

  // All metrics
  metrics.forEach(metric => {
    const score = evaluation[`${metric.key}_score` as keyof ShiftaskProviderEvaluation] as number;
    const comments = evaluation[`${metric.key}_comments` as keyof ShiftaskProviderEvaluation] as string;
    
    doc.setFontSize(12);
    doc.text(`${metric.labelEl}: ${score}/5`, 20, yPos);
    yPos += 7;
    
    if (comments) {
      doc.setFontSize(10);
      const lines = doc.splitTextToSize(`Σχόλια: ${comments}`, 170);
      doc.text(lines, 20, yPos);
      yPos += lines.length * 5 + 5;
    }

    // Add mitigation if exists and requested
    if (includeMitigations && evaluation.admin_mitigations?.[metric.key]) {
      doc.setFontSize(10);
      doc.setTextColor(0, 100, 200);
      const mitigationLines = doc.splitTextToSize(
        `Σχέδιο Δράσης ${appName}: ${evaluation.admin_mitigations[metric.key]}`,
        170
      );
      doc.text(mitigationLines, 20, yPos);
      yPos += mitigationLines.length * 5 + 3;

      // Add client comment on mitigation if exists
      if (evaluation.client_mitigation_comments?.[metric.key]) {
        doc.setTextColor(100, 100, 100);
        const clientLines = doc.splitTextToSize(
          `Ανατροφοδότηση Πελάτη: ${evaluation.client_mitigation_comments[metric.key]}`,
          170
        );
        doc.text(clientLines, 20, yPos);
        yPos += clientLines.length * 5 + 5;
      }
      
      doc.setTextColor(0, 0, 0);
    }

    yPos += 5;

    // Add new page if needed
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
    }
  });

  // Overall satisfaction
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(12);
  doc.text(`Συνολική Ικανοποίηση: ${evaluation.overall_satisfaction}/5`, 20, yPos);
  yPos += 10;

  // Qualitative feedback
  if (evaluation.what_working_well) {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    doc.setFontSize(12);
    doc.text('Τι λειτουργεί καλά:', 20, yPos);
    yPos += 7;
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(evaluation.what_working_well, 170);
    doc.text(lines, 20, yPos);
    yPos += lines.length * 5 + 10;
  }

  if (evaluation.improvement_areas) {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    doc.setFontSize(12);
    doc.text('Περιοχές βελτίωσης:', 20, yPos);
    yPos += 7;
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(evaluation.improvement_areas, 170);
    doc.text(lines, 20, yPos);
    yPos += lines.length * 5 + 10;
  }

  if (evaluation.additional_comments) {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    doc.setFontSize(12);
    doc.text('Πρόσθετα σχόλια:', 20, yPos);
    yPos += 7;
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(evaluation.additional_comments, 170);
    doc.text(lines, 20, yPos);
    yPos += lines.length * 5 + 10;
  }

  // Final questions
  if (yPos > 260) {
    doc.addPage();
    yPos = 20;
  }
  doc.setFontSize(10);
  doc.text(`Θα συνιστούσε τον ${appName}: ${evaluation.would_recommend ? 'Ναι' : 'Όχι'}`, 20, yPos);
  yPos += 7;
  doc.text(`Πιθανότητα ανανέωσης: ${evaluation.likely_to_renew ? 'Ναι' : 'Όχι'}`, 20, yPos);

  // Download
  doc.save(`${appName}-evaluation-${evaluation.evaluation_period}-${evaluation.evaluation_year}.pdf`);
};
